export interface PadTransparentSafeAreaOptions {
  /**
   * Ratio of how much of the final canvas the original image may occupy.
   * Example: 0.6 means the original image fits within 60% of the output size.
   */
  safeAreaRatio: number;
}

function clampSafeAreaRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return 1;
  return Math.min(1, Math.max(0.01, ratio));
}

async function blobFromCanvas(
  canvas: HTMLCanvasElement,
  type: string,
): Promise<Blob | null> {
  return await new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), type);
  });
}

/**
 * Pads an image by drawing it centered on a larger transparent canvas, producing a PNG.
 * This is useful for Android splash/logo safe-area requirements.
 */
export async function padTransparentSafeAreaPng(
  file: File,
  {safeAreaRatio}: PadTransparentSafeAreaOptions,
): Promise<File> {
  const ratio = clampSafeAreaRatio(safeAreaRatio);

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    const maxSide = Math.max(bitmap.width, bitmap.height);
    if (!maxSide || !Number.isFinite(maxSide)) return file;

    const outSide = Math.max(1, Math.round(maxSide / ratio));

    const canvas = document.createElement('canvas');
    canvas.width = outSide;
    canvas.height = outSide;

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.clearRect(0, 0, outSide, outSide);
    const x = Math.round((outSide - bitmap.width) / 2);
    const y = Math.round((outSide - bitmap.height) / 2);
    ctx.drawImage(bitmap, x, y);

    const blob = await blobFromCanvas(canvas, 'image/png');
    if (!blob) return file;

    const name = file.name.replace(/\.\w+$/, '') || 'image';
    return new File([blob], `${name}.png`, {type: 'image/png'});
  } finally {
    bitmap.close();
  }
}
